/**
 * Cross-cutting lifecycle wiring (features #6, #8, #9). Renders nothing.
 *
 *  - Hides the native splash once React has mounted.
 *  - Logs app_open on mount and session_end + flushes on pause/unload.
 *  - Initializes push AFTER the user is approved/signed in, wires foreground
 *    toasts and deep-link navigation (product/{id}, category/{name}).
 *  - Listens for deep-link messages posted by the web service worker.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SplashScreen as CapSplash } from '@capacitor/splash-screen';
import { useAuth } from './features/auth/AuthContext';
import { useToast } from './components/Toast';
import { useAppResume } from './hooks/useAppResume';
import { useAppConfig } from './hooks/useAppConfig';
import { logActivity, flushNow } from './lib/activity/activityLogger';
import { startPresence, stopPresence, setPresenceState } from './lib/presence';
import { initPush, normalizeDeepLink, type PushDeepLink } from './lib/push';
import { addOwnFcmToken, touchLastActive } from './lib/repo';
import { isNative, getInstalledApp } from './lib/platform';
import { throttleGate } from './lib/localCache';

export function AppLifecycle(): JSX.Element | null {
  const { fbUser, status } = useAuth();
  const config = useAppConfig();
  // Presence heartbeat runs ONLY while an admin has the Live monitor open
  // (app_config.livePresenceUntil) — otherwise it would be the single most
  // expensive write in the system for data nobody is looking at.
  const liveWatching = (config?.livePresenceUntil ?? 0) > Date.now();
  const navigate = useNavigate();
  const toast = useToast();
  const pushTeardownRef = useRef<(() => void) | null>(null);

  // Hide native splash after mount.
  useEffect(() => {
    if (isNative) void CapSplash.hide();
  }, []);

  // Record "last active" — and which build is installed — once per app session,
  // as soon as the profile exists.
  const touchedRef = useRef<string | null>(null);
  useEffect(() => {
    const uid = fbUser?.uid;
    if (!uid || status === 'unknown' || touchedRef.current === uid) return;
    touchedRef.current = uid;
    void (async () => {
      const installed = await getInstalledApp();
      // Hourly is plenty for "last active" and keeps 100-opens-a-day dealers from
      // costing 100 writes each. The build is part of the key so a fresh Play
      // update is recorded on its very first open rather than up to an hour later.
      const key = `lastActive3.${uid}.${installed.channel}.${installed.build ?? 0}`;
      if (!throttleGate(key, 60 * 60 * 1000)) return;
      await touchLastActive(uid, installed);
    })().catch(() => undefined);
  }, [fbUser, status]);

  // ANDROID BACK BUTTON: registering a listener replaces Capacitor's default
  // history-walk (which our route guards immediately undo, making back feel
  // dead). On a top-level screen the button CLOSES the app; anywhere deeper it
  // navigates back within the app.
  useEffect(() => {
    if (!isNative) return;
    let remove: (() => void) | null = null;
    void import('@capacitor/app').then(({ App }) => {
      const handle = App.addListener('backButton', () => {
        const hash = window.location.hash;
        const topLevel =
          hash === '' ||
          hash === '#/' ||
          hash.startsWith('#/home') ||
          hash.startsWith('#/category') ||
          hash.startsWith('#/login') ||
          hash.startsWith('#/under-review') ||
          hash.startsWith('#/blocked');
        if (topLevel) {
          void App.exitApp();
        } else {
          window.history.back();
        }
      });
      void handle.then((h) => {
        remove = () => void h.remove();
      });
    });
    return () => {
      if (remove) remove();
    };
  }, []);

  // Activity: app_open on mount; session_end + flush on unload. On unload also
  // mark presence 'background' (closing/hiding) so the Live monitor drops us
  // out of "online" without waiting for the heartbeat to lapse.
  useEffect(() => {
    logActivity('app_open');
    const onUnload = () => {
      logActivity('session_end');
      void flushNow();
      setPresenceState('background');
    };
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('pagehide', onUnload);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);

  // On resume, mark 'online'; on pause, flush activity and mark 'background'.
  // Backed by Capacitor App appStateChange (native) + document.visibilitychange
  // (web), so backgrounding/hiding flips presence promptly either way.
  useAppResume(
    () => {
      logActivity('app_open');
      setPresenceState('online');
    },
    () => {
      logActivity('session_end');
      void flushNow();
      setPresenceState('background');
    },
  );

  // Route a deep link (from push tap or SW message).
  const goDeepLink = useRef((link: PushDeepLink) => {
    const path = link.path.startsWith('/') ? link.path : `/${link.path}`;
    navigate(path);
  });

  // Web service worker → deep-link postMessage bridge.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; path?: string } | undefined;
      if (data?.type === 'deepLink') {
        const link = normalizeDeepLink(data.path);
        if (link) goDeepLink.current(link);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, []);

  // Presence heartbeat: run only while signed in AND approved (LIVE PRESENCE).
  // AuthContext supplies the identity; here we own the heartbeat lifecycle.
  useEffect(() => {
    if (!fbUser || status !== 'approved' || !liveWatching) return;
    startPresence();
    return () => stopPresence();
  }, [fbUser, status, liveWatching]);

  // Initialize push once the user is signed in AND approved (feature #8).
  useEffect(() => {
    if (!fbUser || status !== 'approved') return;
    const uid = fbUser.uid;
    let cancelled = false;

    void initPush({
      onToken: (token) => {
        // FREE-TIER MODEL: no callable — persist the device token by writing
        // the caller's own users/{uid} doc directly (rules allow the self-
        // update since status/id are unchanged; FCM_TOKEN_LIMIT is enforced
        // client-side). Best-effort: a failure just means no push to this
        // device until the next registration.
        void addOwnFcmToken(uid, token).catch((e) => {
          console.warn('[push] addOwnFcmToken failed (non-fatal):', e);
        });
      },
      onForeground: ({ title, body, deepLink }) => {
        toast.show({
          title: title ?? 'ANYWAY IT SOLUTIONS',
          body: body ?? '',
          onClick: deepLink
            ? () => {
                const link = normalizeDeepLink(deepLink);
                if (link) goDeepLink.current(link);
              }
            : undefined,
        });
      },
      onOpen: (link) => goDeepLink.current(link),
    }).then((teardown) => {
      if (cancelled) teardown();
      else pushTeardownRef.current = teardown;
    });

    return () => {
      cancelled = true;
      pushTeardownRef.current?.();
      pushTeardownRef.current = null;
    };
  }, [fbUser, status, toast]);

  return null;
}
